import { createServerFn } from "@tanstack/react-start";

import { authMiddleware } from "~/lib/auth-middleware";
import { requireClerkCoachAuth } from "~/lib/coach-middleware";
import { S3Middleware } from "~/lib/s3-middleware";
import {
  UpdateCategoryOfLifeWithImageSchema,
  CreateCategoryOfLifeSchema,
  DeleteCategoryOfLifeSchema,
  GetCategoryOfLifeUrlSchema,
  GetSharedCategoriesSchema,
  GetCategoryByIdSchema,
} from "~/lib/schemas/category-of-life";

export const getCategoryUploadUrlFn = createServerFn({ method: "POST" })
  .middleware([S3Middleware, authMiddleware])
  .validator(GetCategoryOfLifeUrlSchema)
  .handler(async ({ data, context }) => {
    const { buildCategoryItemKey, getPresignedUploadUrl } =
      await import("~/lib/server/s3");
    const userId = context.user.id;
    const categoryId = data.categoryId;

    if (!data.filename || !data.contentType)
      throw new Error("File info is required");

    const key = buildCategoryItemKey({
      userId,
      categoryId: categoryId,
      ext: data.filename.split(".").pop() || "bin",
    });

    const signed = await getPresignedUploadUrl({
      key,
      contentType: data.contentType,
    });

    const s3ImageUrl = `s3://${key}`;

    return {
      categoryId: categoryId,
      key: key,
      signedUrl: signed.url,
      s3ImageUrl,
    };
  });

export const getCategoriesOfLifeFn = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .handler(async ({ context }) => {
    const { getPublicUrl } = await import("~/lib/server/s3");
    const { getCategoryOfLife } = await import("~/lib/server/category-of-life");
    const userId = context.user.id;
    const rows = await getCategoryOfLife(userId);

    return rows.map((row) => {
      const key = row.imageUrl;
      return {
        ...row,
        imageUrl: key ? getPublicUrl(key) : null,
        imageKey: key ?? null,
      };
    });
  });

export const finalizeCategoryFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(CreateCategoryOfLifeSchema)
  .handler(async ({ data, context }) => {
    const { createCategoryOfLife } =
      await import("~/lib/server/category-of-life");
    const userId = context.user.id;
    return await createCategoryOfLife(
      {
        id: data.categoryId,
        userId,
        imageUrl: data.imageUrl,
        encryptedName: data.encryptedName,
        encryptedShortDescription: data.encryptedShortDescription,
        iv: data.iv,
      },
      data.keyGrants,
    );
  });

export const updateCategoryFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(UpdateCategoryOfLifeWithImageSchema)
  .handler(async ({ data, context }) => {
    const { updateCategoryOfLife } =
      await import("~/lib/server/category-of-life");
    return await updateCategoryOfLife(
      {
        id: data.id,
        userId: context.user.id,
        imageUrl: data.imageUrl,
        encryptedName: data.encryptedName,
        encryptedShortDescription: data.encryptedShortDescription,
        iv: data.iv,
      },
      data.keyGrants,
      data.shareCategoryName,
    );
  });

export const deleteCategoryOfLifeAndFileFn = createServerFn({ method: "POST" })
  .middleware([S3Middleware, authMiddleware])
  .validator(DeleteCategoryOfLifeSchema)
  .handler(async ({ data, context }) => {
    const { deleteCustomCategory } =
      await import("~/lib/server/category-of-life");
    await deleteCustomCategory(data.id, context.user.id);
    return { ok: true };
  });

export const getCategoryOfLifeFn = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .handler(async ({ context }) => {
    const { getCategoryOfLife } = await import("~/lib/server/category-of-life");
    const userId = context.user.id;
    return getCategoryOfLife(userId);
  });

export const getSharedCategoriesFn = createServerFn({ method: "GET" })
  .middleware([requireClerkCoachAuth])
  .validator(GetSharedCategoriesSchema)
  .handler(async ({ data, context }) => {
    const { getPublicUrl } = await import("~/lib/server/s3");
    const { getSharedCategories } =
      await import("~/lib/server/category-of-life");
    const coachId = context.coach.id;
    const rows = await getSharedCategories(data.userId, coachId);

    return rows.map((row) => {
      const category = row.CategoryOfLife;
      const key = category.imageUrl;
      return {
        ...category,
        imageUrl: key ? getPublicUrl(key) : null,
        imageKey: key ?? null,
      };
    });
  });

export const getCategoryByIdFn = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(GetCategoryByIdSchema)
  .handler(async ({ data, context }) => {
    const { getPublicUrl } = await import("~/lib/server/s3");
    const { categoryHasSharedGoals, getCategoryOfLifeById } =
      await import("~/lib/server/category-of-life");
    const userId = context.user.id;
    const category = await getCategoryOfLifeById(data.id);

    // Missing row is an expected miss (stale link / deleted) — return null so
    // the route loader can render notFound without paging Sentry.
    if (!category) return null;

    // Ownership failure is a real authz signal — keep throwing.
    if (category.userId !== userId) {
      throw new Error("Unauthorized to view this category");
    }

    return {
      ...category,
      imageUrl: category.imageUrl ? getPublicUrl(category.imageUrl) : null,
      imageKey: category.imageUrl,
      hasSharedGoals: await categoryHasSharedGoals(category.id, userId),
    };
  });

export const getSharedCategoryByIdFn = createServerFn({ method: "GET" })
  .middleware([requireClerkCoachAuth])
  .validator(GetCategoryByIdSchema)
  .handler(async ({ data, context }) => {
    const { getSharedCategoryById } =
      await import("~/lib/server/category-of-life");
    const coachId = context.coach.id;
    const category = await getSharedCategoryById(data.id, coachId);
    if (!category) return null;

    const { getPublicUrl } = await import("~/lib/server/s3");

    return {
      ...category,
      imageUrl: category.imageUrl ? getPublicUrl(category.imageUrl) : null,
      imageKey: category.imageUrl,
    };
  });
